// Data Service Layer - Abstracts between Live and Demo modes
import type { FeatureMode } from '@/types';
import type { DataMode } from '@/contexts/DataModeContext';
import type { PaginationParams, PaginatedResponse } from '@/types/pagination';
import { paginateArray } from '@/types/pagination';
import * as mockData from './mock-data';
import { apiFetch } from './api-error-handler';

// API base URL - uses environment variable or defaults to /api for same-origin requests
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Storage keys
const FEATURE_MODE_KEY = 'access-pm-feature-modes';
const SENSITIVE_DATA_KEY = 'access-pm-sensitive-data-visible';
const DATA_MODE_KEY = 'evo-data-mode';

interface FeatureModes {
  [featureId: string]: FeatureMode;
}

// Get global data mode from localStorage
export function getDataMode(): DataMode {
  if (typeof window === 'undefined') return 'demo';
  
  try {
    const stored = localStorage.getItem(DATA_MODE_KEY);
    if (stored === 'demo' || stored === 'live') {
      return stored;
    }
    return 'demo';
  } catch {
    return 'demo';
  }
}

// Get feature mode from localStorage (legacy - kept for backward compatibility)
export function getFeatureMode(featureId: string): FeatureMode {
  if (typeof window === 'undefined') return 'demo';
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    if (!stored) return 'demo';
    
    const modes: FeatureModes = JSON.parse(stored);
    return modes[featureId] || 'demo';
  } catch {
    return 'demo';
  }
}

// Set feature mode
export function setFeatureMode(featureId: string, mode: FeatureMode): void {
  if (typeof window === 'undefined') return;
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    const modes: FeatureModes = stored ? JSON.parse(stored) : {};
    modes[featureId] = mode;
    localStorage.setItem(FEATURE_MODE_KEY, JSON.stringify(modes));
    
    // Trigger storage event for other tabs/components
    window.dispatchEvent(new CustomEvent('featureModeChanged', { 
      detail: { featureId, mode } 
    }));
  } catch (error) {
    console.error('Failed to set feature mode:', error);
  }
}

// Get all feature modes
export function getAllFeatureModes(): FeatureModes {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Sensitive data visibility
export function getSensitiveDataVisible(): boolean {
  if (typeof window === 'undefined') return true;
  
  try {
    const stored = localStorage.getItem(SENSITIVE_DATA_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

export function setSensitiveDataVisible(visible: boolean): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(SENSITIVE_DATA_KEY, String(visible));
    window.dispatchEvent(new CustomEvent('sensitiveDataVisibilityChanged', { 
      detail: { visible } 
    }));
  } catch (error) {
    console.error('Failed to set sensitive data visibility:', error);
  }
}

// Data fetching functions - route based on global data mode
export async function fetchProjects(
  featureId: string = 'projects',
  paginationParams?: PaginationParams
): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // If pagination params provided, return paginated response
    if (paginationParams) {
      const { page, pageSize } = paginationParams;
      return paginateArray(mockData.mockProjects, page, pageSize);
    }
    
    // Otherwise return all projects (backward compatibility)
    return mockData.mockProjects;
  } else {
    // Live mode - call the API
    const response = await apiFetch<any>(`${API_BASE_URL}/projects`);
    
    // Validate that the response is an array
    // The API should return a JSON array, but if it returns an object or other type,
    // we need to handle it gracefully to prevent ".filter is not a function" errors
    let projects: any[];
    if (Array.isArray(response)) {
      projects = response;
    } else if (response && typeof response === 'object') {
      // Handle common wrapper patterns: { items: [...] }, { data: [...] }, { value: [...] }, { $values: [...] }
      if (Array.isArray(response.items)) {
        projects = response.items;
      } else if (Array.isArray(response.data)) {
        projects = response.data;
      } else if (Array.isArray(response.value)) {
        projects = response.value;
      } else if (Array.isArray(response.$values)) {
        // .NET ReferenceHandler.Preserve format
        projects = response.$values;
      } else {
        console.error('API returned unexpected format for projects:', response);
        throw new Error(`API returned unexpected format for projects. Expected an array but received: ${typeof response}`);
      }
    } else {
      console.error('API returned unexpected format for projects:', response);
      throw new Error(`API returned unexpected format for projects. Expected an array but received: ${typeof response}`);
    }
    
    // If pagination params provided, paginate the results client-side
    // (In a production app, pagination would be handled server-side)
    if (paginationParams) {
      const { page, pageSize } = paginationParams;
      return paginateArray(projects, page, pageSize);
    }
    
    return projects;
  }
}

export async function fetchClients(featureId: string = 'clients') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockClients;
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/clients`);
  }
}

export async function fetchEmployees(featureId: string = 'employees') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockEmployees;
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/employees`);
  }
}

export async function fetchResourceAllocations(featureId: string = 'resources') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 250));
    return mockData.mockResourceAllocations;
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/resourceallocations`);
  }
}

export async function fetchWIPReports(featureId: string = 'wip-reporting') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    return mockData.mockWIPReports;
  } else {
    // WIP Reports are computed from project data - fetch projects and compute WIP
    const projects = await apiFetch<any[]>(`${API_BASE_URL}/projects`);
    
    // Transform projects into WIP report format
    // Now uses clientNames from the project (populated by stored procedure)
    return projects.map(project => {
      return {
        projectId: project.id,
        projectName: project.name,
        clientName: project.clientNames || 'Unknown',
        costsIncurred: project.actualLabour + project.actualExpense,
        profitEarned: project.actualRevenue - (project.actualLabour + project.actualExpense),
        amountBilled: project.actualRevenue,
        wipAmount: project.wipAmount,
        agingDays: Math.floor((new Date().getTime() - new Date(project.startDate).getTime()) / (1000 * 60 * 60 * 24)),
        readyToBill: project.wipAmount > 0,
        overBilling: project.wipAmount < 0 ? Math.abs(project.wipAmount) : 0,
        underBilling: project.wipAmount > 0 ? project.wipAmount : 0,
      };
    });
  }
}

export async function fetchUtilisationData(featureId: string = 'utilisation') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockData.mockUtilisationData;
  } else {
    // Utilisation data is computed from employees and time entries
    const employees = await apiFetch<any[]>(`${API_BASE_URL}/employees`);
    const timeEntries = await apiFetch<any[]>(`${API_BASE_URL}/timeentries`);
    
    // Calculate utilisation for each employee
    const standardHoursPerMonth = 160; // Assuming 40 hours/week * 4 weeks
    
    return employees.map(employee => {
      const employeeTimeEntries = timeEntries.filter(te => te.employeeId === employee.id);
      const billableHours = employeeTimeEntries
        .filter(te => te.billable)
        .reduce((sum, te) => sum + te.hours, 0);
      const totalHours = employeeTimeEntries.reduce((sum, te) => sum + te.hours, 0);
      const utilisationPercent = standardHoursPerMonth > 0 
        ? (billableHours / standardHoursPerMonth) * 100 
        : 0;
      const targetPercent = 75; // Default target
      
      // Determine status based on utilisation vs target
      const status: 'under' | 'target' | 'over' = 
        utilisationPercent < targetPercent - 10 ? 'under' : 
        utilisationPercent > targetPercent + 10 ? 'over' : 'target';
      
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        role: employee.role,
        department: employee.department,
        billableHours,
        availableHours: standardHoursPerMonth,
        utilisationPercent,
        targetPercent,
        status,
      };
    });
  }
}

export async function fetchPortfolioSummary(featureId: string = 'portfolio') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 350));
    return mockData.mockPortfolioSummary;
  } else {
    // Portfolio summary is computed from projects data
    const projects = await apiFetch<any[]>(`${API_BASE_URL}/projects`);
    
    // Calculate portfolio summary from projects
    const activeProjects = projects.filter(p => p.status === 'in-progress').length;
    const portfolioValue = projects.reduce((sum, p) => sum + p.budgetRevenue, 0);
    const totalWIP = projects.reduce((sum, p) => sum + p.wipAmount, 0);
    const avgMarginPercent = projects.length > 0 
      ? projects.reduce((sum, p) => sum + p.marginPercent, 0) / projects.length 
      : 0;
    
    // Count projects by status
    const projectsByStatus: Record<string, number> = {
      'not-started': 0,
      'in-progress': 0,
      'on-hold': 0,
      'completed': 0,
      'cancelled': 0,
    };
    projects.forEach(p => {
      if (projectsByStatus[p.status] !== undefined) {
        projectsByStatus[p.status]++;
      }
    });
    
    // Count projects by health
    const projectsByHealth: Record<string, number> = {
      'green': 0,
      'amber': 0,
      'red': 0,
    };
    projects.forEach(p => {
      if (projectsByHealth[p.health] !== undefined) {
        projectsByHealth[p.health]++;
      }
    });
    
    return {
      totalProjects: projects.length,
      activeProjects,
      portfolioValue,
      totalWIP,
      avgMarginPercent,
      avgUtilisationPercent: 75, // Would need time entries to calculate properly
      projectsByStatus,
      projectsByHealth,
    };
  }
}

export async function fetchIntegrationStatus(featureId: string = 'integrations') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 150));
    return mockData.mockIntegrationStatus;
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/integrationstatus`);
  }
}

export async function fetchTasks(featureId: string = 'tasks') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockTasks;
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/tasks`);
  }
}

export async function fetchMilestones(featureId: string = 'milestones') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockMilestones;
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/milestones`);
  }
}

export async function fetchCostCentres(featureId: string = 'costcentres') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/costcentres`);
  }
}

export async function fetchCostCentresByProjectId(projectId: string, featureId: string = 'costcentres') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  } else {
    return apiFetch<any[]>(`${API_BASE_URL}/costcentres/by-project/${projectId}`);
  }
}

// Create a new cost centre
export interface CreateCostCentreData {
  projectId: string;
  code: string;
  name: string;
  type?: string;
  status?: string;
  billable?: boolean;
  owner?: string;
  description?: string;
  costsBudget?: number;
  revenueBudget?: number;
  hoursBudget?: number;
}

export async function createCostCentre(data: CreateCostCentreData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, simulate creation but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return a mock created cost centre with generated ID
    const mockCostCentre = {
      id: `demo-cc-${Date.now()}`,
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || 'Not Started',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: 0,
      lastUpdated: new Date().toISOString(),
    };
    
    return mockCostCentre;
  } else {
    // Live mode - validate required fields before calling API
    if (!data.projectId) {
      throw new Error('Project ID is required to create a cost centre.');
    }
    if (!data.code) {
      throw new Error('Cost centre code is required.');
    }
    if (!data.name) {
      throw new Error('Cost centre name is required.');
    }
    
    // Build the payload matching the backend CostCentre entity
    const costCentrePayload = {
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || 'Not Started',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: 0,
      lastUpdated: new Date().toISOString(),
    };
    
    return apiFetch<any>(`${API_BASE_URL}/costcentres`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(costCentrePayload),
    });
  }
}

// Create a new project
// Note: Fields are optional here because the form validation (which respects Project Creation Settings)
// is the source of truth for which fields are required
export interface CreateProjectData {
  name: string;
  projectCode?: string;
  description?: string;
  status?: string;
  priority?: string;
  type?: string;
  startDate?: Date;
  endDate?: Date;
  projectManager?: string;
  clients?: string[];
  budget?: string;
}

export async function createProject(data: CreateProjectData): Promise<any> {
  const mode = getDataMode();
  
  // Extract optional fields with defaults - validation is handled by the form based on Project Creation Settings
  const clientIds = data.clients || [];
  const projectManagerId = data.projectManager || '';
  
  if (mode === 'demo') {
    // In demo mode, simulate creation but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return a mock created project with generated ID
    const mockProject = {
      id: `demo-${Date.now()}`,
      name: data.name,
      clientIds: clientIds.join(','),
      clientNames: clientIds.map(id => `Client ${id}`).join(', '),
      projectManagerId: projectManagerId || 'demo-manager',
      type: data.type || '',
      status: data.status || 'Not Started',
      health: 'green',
      startDate: data.startDate?.toISOString() || '',
      endDate: data.endDate?.toISOString() || '',
      description: data.description || '',
      budgetLabour: 0,
      budgetExpense: 0,
      budgetRevenue: data.budget ? parseFloat(data.budget.replace(/[^0-9.-]+/g, '')) : 0,
      actualLabour: 0,
      actualExpense: 0,
      actualRevenue: 0,
      budgetHours: 0,
      actualHours: 0,
      hoursVariance: 0,
      hoursVariancePercent: 0,
      totalBudget: 0,
      totalActual: 0,
      variance: 0,
      variancePercent: 0,
      marginPercent: 0,
      completionPercent: 0,
      revenueRecognitionMethod: 'percentage-of-completion',
      wipAmount: 0,
      unbilledAmount: 0,
    };
    
    return mockProject;
  } else {
    // Live mode - call the API
    // Use conditional spreads to omit optional fields when not provided
    // This prevents sending empty strings for DateTime fields which the API can't parse
    const projectPayload = {
      name: data.name,
      ...(clientIds.length > 0 ? { clientIds: clientIds.join(',') } : {}),
      ...(projectManagerId ? { projectManagerId } : {}),
      ...(data.type ? { type: data.type } : {}),
      ...(data.status ? { status: data.status } : {}),
      health: 'green',
      ...(data.startDate ? { startDate: data.startDate.toISOString() } : {}),
      ...(data.endDate ? { endDate: data.endDate.toISOString() } : {}),
      ...(data.description ? { description: data.description } : {}),
      budgetLabour: 0,
      budgetExpense: 0,
      budgetRevenue: data.budget ? parseFloat(data.budget.replace(/[^0-9.-]+/g, '')) : 0,
      actualLabour: 0,
      actualExpense: 0,
      actualRevenue: 0,
      budgetHours: 0,
      actualHours: 0,
      hoursVariance: 0,
      hoursVariancePercent: 0,
      totalBudget: 0,
      totalActual: 0,
      variance: 0,
      variancePercent: 0,
      marginPercent: 0,
      completionPercent: 0,
      revenueRecognitionMethod: 'percentage-of-completion',
      wipAmount: 0,
      unbilledAmount: 0,
    };
    
    return apiFetch<any>(`${API_BASE_URL}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectPayload),
    });
  }
}

// Helper to get project with related data
export async function fetchProjectDetails(projectId: string, featureId: string = 'projects') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    const project = mockData.getProjectById(projectId);
    if (!project) return null;
    
    // Get all clients for this project (now supports multiple)
    const clientIds = project.clientIds?.split(',').filter(Boolean) || [];
    const projectClients = clientIds.map(id => mockData.getClientById(id)).filter(Boolean);
    const manager = mockData.getEmployeeById(project.projectManagerId);
    const allocations = mockData.getAllocationsByProject(projectId);
    const team = allocations.map(a => mockData.getEmployeeById(a.employeeId)).filter(Boolean);
    
    return {
      project,
      clients: projectClients,
      manager,
      allocations,
      team,
    };
  } else {
    // Fetch project details from API
    const project = await apiFetch<any>(`${API_BASE_URL}/projects/${projectId}`);
    if (!project) return null;
    
    // Fetch related data in parallel
    const [clients, employees, allocations] = await Promise.all([
      apiFetch<any[]>(`${API_BASE_URL}/clients`),
      apiFetch<any[]>(`${API_BASE_URL}/employees`),
      apiFetch<any[]>(`${API_BASE_URL}/resourceallocations/by-project/${projectId}`),
    ]);
    
    // Get all clients for this project (now supports multiple)
    const clientIds = project.clientIds?.split(',').filter(Boolean) || [];
    const projectClients = clientIds.map((id: string) => clients.find(c => c.id === id)).filter(Boolean);
    const manager = employees.find(e => e.id === project.projectManagerId);
    const team = allocations
      .map(a => employees.find(e => e.id === a.employeeId))
      .filter(Boolean);
    
    return {
      project,
      clients: projectClients,
      manager,
      allocations,
      team,
    };
  }
}
